class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.isInline = this.dataset.inline === "true";
    this.currentSearchTerm = "";
    this.STORAGE_KEY = "otticain_last_searches";
    this.MAX_SEARCHES = 3;
    this.RESULTS_LIMIT = parseInt(this.dataset.limit) || 12;

    // Select the appropriate results container based on variant
    if (this.isInline) {
      this.predictiveSearchResults = this.querySelector(
        "#header-predictive-search-results",
      );
    } else {
      this.predictiveSearchResults = this.querySelector(
        "#predictive-search-target",
      );
    }

    this.input.addEventListener(
      "input",
      this.debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    // Show last searches when input is focused
    this.input.addEventListener("focus", () => {
      this.showLastSearchesOnFocus();
    });

    // For inline variant, close on Escape key
    if (this.isInline) {
      this.input.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          this.close();
          this.input.blur();
        }
      });
    }
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.showDefaultContent();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    this.currentSearchTerm = searchTerm;

    fetch(
      `/search/suggest?q=${searchTerm}&resources[type]=product,collection&resources[limit]=${this.RESULTS_LIMIT}&section_id=predictive-search-results`,
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector(
            "#shopify-section-predictive-search-results",
          ).innerHTML;
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        this.attachClickListeners();
        this.displayLastSearches();
        this.open();
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  attachClickListeners() {
    const links = this.predictiveSearchResults.querySelectorAll("a");
    links.forEach((link) => {
      link.addEventListener("click", () => {
        if (this.currentSearchTerm) {
          this.saveSearch(this.currentSearchTerm);
        }
      });
    });
  }

  getLastSearches() {
    try {
      const searches = localStorage.getItem(this.STORAGE_KEY);
      return searches ? JSON.parse(searches) : [];
    } catch (e) {
      console.error("Error reading last searches:", e);
      return [];
    }
  }

  saveSearch(searchTerm) {
    try {
      let searches = this.getLastSearches();

      // Remove if already exists to avoid duplicates
      searches = searches.filter((s) => s !== searchTerm);

      // Add to the beginning
      searches.unshift(searchTerm);

      // Keep only the last MAX_SEARCHES
      searches = searches.slice(0, this.MAX_SEARCHES);

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(searches));
    } catch (e) {
      console.error("Error saving search:", e);
    }
  }

  displayLastSearches() {
    const container = this.predictiveSearchResults.querySelector(
      "#last-searches-container",
    );
    if (!container) return;

    const lastSearches = this.getLastSearches();

    if (lastSearches.length === 0) {
      container.innerHTML = "";
      return;
    }

    const html = `
      <div class="last-searches">
        <p>Ultime ricerche</p>
        <ul>
          ${lastSearches.map((search) => `<li><a href="/search?q=${encodeURIComponent(search)}">${search}</a></li>`).join("")}
        </ul>
      </div>
    `;

    container.innerHTML = html;
  }

  showLastSearchesOnFocus() {
    const searchTerm = this.input.value.trim();
    if (!searchTerm.length) {
      this.showDefaultContent();
    }
  }

  showDefaultContent() {
    // Clear the results section but keep the container
    const resultsDiv = this.predictiveSearchResults.querySelector(
      "#predictive-search-results",
    );
    if (resultsDiv) {
      resultsDiv.innerHTML = "";
    }

    // Display last searches and other static content
    const container = this.predictiveSearchResults.querySelector(
      "#last-searches-container",
    );
    if (!container) {
      this.open();
      return;
    }

    const lastSearches = this.getLastSearches();

    let html = '<div class="default-search-content">';

    // Add last searches if available
    if (lastSearches.length > 0) {
      html += `
        <div class="last-searches">
          <p>Ultime ricerche</p>
          <ul>
            ${lastSearches.map((search) => `<li><a href="/search?q=${encodeURIComponent(search)}">${search}</a></li>`).join("")}
          </ul>
        </div>
      `;
    }

    // Add placeholder for other static content
    html += '<div class="static-content-placeholder"></div>';
    html += "</div>";

    container.innerHTML = html;
    this.open();
  }

  open() {
    this.input.setAttribute("aria-expanded", "true");

    // Sync with Alpine.js state if inline variant
    if (this.isInline) {
      // Access Alpine component data directly from this element
      if (this._x_dataStack && this._x_dataStack[0]) {
        this._x_dataStack[0].isSearchOpen = true;
      }
    } else {
      this.predictiveSearchResults.style.display = "block";
    }
  }

  close() {
    this.input.setAttribute("aria-expanded", "false");

    // Sync with Alpine.js state if inline variant
    if (this.isInline) {
      // Access Alpine component data directly from this element
      if (this._x_dataStack && this._x_dataStack[0]) {
        this._x_dataStack[0].isSearchOpen = false;
      }
    } else {
      this.predictiveSearchResults.style.display = "none";
    }
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
customElements.define("predictive-search", PredictiveSearch);
